/**
 * Generic coalescing undo/redo stack — design §1 promises unlimited undo
 * within a session. The cap is a memory backstop, not a behavior: entries are
 * ~100-byte poses, so 1000 non-coalesced commands is beyond any real session
 * while still bounding a runaway.
 */
export interface UndoEntry<T> {
  key: string
  before: T
  after: T
  at: number
}
export interface UndoStack<T> {
  entries: UndoEntry<T>[]
  /** How many entries are currently applied; the redo tail sits above it. */
  cursor: number
}
export const UNDO_LIMIT = 1000
export const COALESCE_WINDOW_MS = 800

export function emptyUndo<T>(): UndoStack<T> {
  return { entries: [], cursor: 0 }
}

export function pushUndo<T>(stack: UndoStack<T>, entry: UndoEntry<T>): UndoStack<T> {
  const kept = stack.entries.slice(0, stack.cursor)
  const last = kept[kept.length - 1]
  if (last && last.key === entry.key && entry.at - last.at < COALESCE_WINDOW_MS) {
    const merged = [...kept.slice(0, -1), { ...last, after: entry.after, at: entry.at }]
    return { entries: merged, cursor: merged.length }
  }
  const entries = [...kept, entry].slice(-UNDO_LIMIT)
  return { entries, cursor: entries.length }
}

export function undo<T>(
  stack: UndoStack<T>,
): { stack: UndoStack<T>; restore: { key: string; value: T } } | null {
  if (stack.cursor === 0) return null
  const entry = stack.entries[stack.cursor - 1]!
  return {
    stack: { ...stack, cursor: stack.cursor - 1 },
    restore: { key: entry.key, value: entry.before },
  }
}

export function redo<T>(
  stack: UndoStack<T>,
): { stack: UndoStack<T>; restore: { key: string; value: T } } | null {
  if (stack.cursor >= stack.entries.length) return null
  const entry = stack.entries[stack.cursor]!
  return {
    stack: { ...stack, cursor: stack.cursor + 1 },
    restore: { key: entry.key, value: entry.after },
  }
}
